// Offline Data Synchronization Utility

const DB_NAME = 'FlavorFeedPOSOffline';
const DB_VERSION = 1;

// Store names
const STORES = {
  ORDERS: 'pending_orders',
  PAYMENTS: 'pending_payments',
  MENU_ITEMS: 'menu_items_cache',
  SETTINGS: 'settings_cache'
};

// Open IndexedDB
function openDatabase() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Create object stores if they don't exist
      if (!db.objectStoreNames.contains(STORES.ORDERS)) {
        db.createObjectStore(STORES.ORDERS, { keyPath: 'id', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(STORES.PAYMENTS)) {
        db.createObjectStore(STORES.PAYMENTS, { keyPath: 'id', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(STORES.MENU_ITEMS)) {
        db.createObjectStore(STORES.MENU_ITEMS, { keyPath: '_id' });
      }
      if (!db.objectStoreNames.contains(STORES.SETTINGS)) {
        db.createObjectStore(STORES.SETTINGS, { keyPath: 'key' });
      }
    };
  });
}

// Save order offline
export async function saveOrderOffline(orderData) {
  try {
    const db = await openDatabase();
    const tx = db.transaction(STORES.ORDERS, 'readwrite');
    const store = tx.objectStore(STORES.ORDERS);
    
    const order = {
      ...orderData,
      offline: true,
      timestamp: Date.now(),
      synced: false
    };
    
    const result = await new Promise((resolve, reject) => {
      const request = store.add(order);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    console.log('✅ Order saved offline:', result);
    return { success: true, id: result, offline: true };
  } catch (error) {
    console.error('❌ Failed to save order offline:', error);
    throw error;
  }
}

// Save payment offline
export async function savePaymentOffline(paymentData) {
  try {
    const db = await openDatabase();
    const tx = db.transaction(STORES.PAYMENTS, 'readwrite');
    const store = tx.objectStore(STORES.PAYMENTS);
    
    const payment = {
      ...paymentData,
      offline: true,
      timestamp: Date.now(),
      synced: false
    };
    
    const result = await new Promise((resolve, reject) => {
      const request = store.add(payment);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    console.log('✅ Payment saved offline:', result);
    return { success: true, id: result, offline: true };
  } catch (error) {
    console.error('❌ Failed to save payment offline:', error);
    throw error;
  }
}

// Get all pending orders
export async function getPendingOrders() {
  try {
    const db = await openDatabase();
    const tx = db.transaction(STORES.ORDERS, 'readonly');
    const store = tx.objectStore(STORES.ORDERS);
    
    const orders = await new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    return orders.filter(order => !order.synced);
  } catch (error) {
    console.error('❌ Failed to get pending orders:', error);
    return [];
  }
}

// Get all pending payments
export async function getPendingPayments() {
  try {
    const db = await openDatabase();
    const tx = db.transaction(STORES.PAYMENTS, 'readonly');
    const store = tx.objectStore(STORES.PAYMENTS);
    
    const payments = await new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    return payments.filter(payment => !payment.synced);
  } catch (error) {
    console.error('❌ Failed to get pending payments:', error);
    return [];
  }
}

// Sync pending orders to server
export async function syncPendingOrders(apiClient) {
  try {
    const pendingOrders = await getPendingOrders();
    console.log(`🔄 Syncing ${pendingOrders.length} pending orders...`);
    
    const db = await openDatabase();
    let successCount = 0;
    
    for (const order of pendingOrders) {
      try {
        // Send to server
        const response = await apiClient.post('/api/pos/orders', {
          ...order,
          offline_id: order.id
        });
        
        if (response.data.success) {
          // Mark as synced
          const tx = db.transaction(STORES.ORDERS, 'readwrite');
          const store = tx.objectStore(STORES.ORDERS);
          order.synced = true;
          order.server_id = response.data.order_id;
          await store.put(order);
          successCount++;
        }
      } catch (error) {
        console.error('❌ Failed to sync order:', order.id, error);
      }
    }
    
    console.log(`✅ Synced ${successCount}/${pendingOrders.length} orders`);
    return { success: true, synced: successCount, total: pendingOrders.length };
  } catch (error) {
    console.error('❌ Sync failed:', error);
    return { success: false, error: error.message };
  }
}

// Sync pending payments to server
export async function syncPendingPayments(apiClient) {
  try {
    const pendingPayments = await getPendingPayments();
    console.log(`🔄 Syncing ${pendingPayments.length} pending payments...`);
    
    const db = await openDatabase();
    let successCount = 0;
    
    for (const payment of pendingPayments) {
      try {
        // Send to server
        const response = await apiClient.post('/api/pos/payments', {
          ...payment,
          offline_id: payment.id
        });
        
        if (response.data.success) {
          // Mark as synced
          const tx = db.transaction(STORES.PAYMENTS, 'readwrite');
          const store = tx.objectStore(STORES.PAYMENTS);
          payment.synced = true;
          payment.server_id = response.data.payment_id;
          await store.put(payment);
          successCount++;
        }
      } catch (error) {
        console.error('❌ Failed to sync payment:', payment.id, error);
      }
    }
    
    console.log(`✅ Synced ${successCount}/${pendingPayments.length} payments`);
    return { success: true, synced: successCount, total: pendingPayments.length };
  } catch (error) {
    console.error('❌ Sync failed:', error);
    return { success: false, error: error.message };
  }
}

// Cache menu items for offline use
export async function cacheMenuItems(menuItems) {
  try {
    const db = await openDatabase();
    const tx = db.transaction(STORES.MENU_ITEMS, 'readwrite');
    const store = tx.objectStore(STORES.MENU_ITEMS);
    
    // Clear existing cache
    await store.clear();
    
    // Add all menu items
    for (const item of menuItems) {
      await store.add(item);
    }
    
    console.log(`✅ Cached ${menuItems.length} menu items for offline use`);
    return true;
  } catch (error) {
    console.error('❌ Failed to cache menu items:', error);
    return false;
  }
}

// Get cached menu items
export async function getCachedMenuItems() {
  try {
    const db = await openDatabase();
    const tx = db.transaction(STORES.MENU_ITEMS, 'readonly');
    const store = tx.objectStore(STORES.MENU_ITEMS);
    
    const items = await new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    console.log(`✅ Retrieved ${items.length} cached menu items`);
    return items;
  } catch (error) {
    console.error('❌ Failed to get cached menu items:', error);
    return [];
  }
}

// Check if online
export function isOnline() {
  return navigator.onLine;
}

// Listen for online/offline events
export function setupConnectivityListeners(onOnline, onOffline) {
  window.addEventListener('online', () => {
    console.log('🟢 Back online!');
    onOnline && onOnline();
  });
  
  window.addEventListener('offline', () => {
    console.log('🔴 Gone offline!');
    onOffline && onOffline();
  });
  
  // Return initial state
  return isOnline();
}

// Get pending items count
export async function getPendingCount() {
  try {
    const orders = await getPendingOrders();
    const payments = await getPendingPayments();
    return {
      orders: orders.length,
      payments: payments.length,
      total: orders.length + payments.length
    };
  } catch (error) {
    console.error('❌ Failed to get pending count:', error);
    return { orders: 0, payments: 0, total: 0 };
  }
}

// Clear synced data (cleanup)
export async function clearSyncedData() {
  try {
    const db = await openDatabase();
    
    // Clear synced orders
    const ordersTx = db.transaction(STORES.ORDERS, 'readwrite');
    const ordersStore = ordersTx.objectStore(STORES.ORDERS);
    const allOrders = await ordersStore.getAll();
    
    for (const order of allOrders) {
      if (order.synced) {
        await ordersStore.delete(order.id);
      }
    }
    
    // Clear synced payments
    const paymentsTx = db.transaction(STORES.PAYMENTS, 'readwrite');
    const paymentsStore = paymentsTx.objectStore(STORES.PAYMENTS);
    const allPayments = await paymentsStore.getAll();
    
    for (const payment of allPayments) {
      if (payment.synced) {
        await paymentsStore.delete(payment.id);
      }
    }
    
    console.log('✅ Cleared synced data');
    return true;
  } catch (error) {
    console.error('❌ Failed to clear synced data:', error);
    return false;
  }
}
